import { useState } from 'react';
import { Head, useAdmin } from '../ui/Shell';
import { Block, Cell, Chart, Grid, Kpis, Seg, Table, type Row } from '../ui/Block';
import { post } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { downloadText } from './Analytics';
import { CATEGORIES, money, pct } from './costShared';

type Bucket = 'day' | 'week' | 'month';
type Range = '30' | '90' | '365';
type Num = Record<string, number>;
interface Running {
  from: string; to: string; bucket: Bucket; categories: string[];
  rows: (Num & { period: string })[]; totals: Num;
  fixed: { name: string; perMonth: number; priced: boolean }[];
  polarFeePct: number; sources: Record<string, string>;
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);

export function Costs() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [bucket, setBucket] = useState<Bucket>('day');
  const [range, setRange] = useState<Range>('30');
  const [busy, setBusy] = useState(false);
  const from = ymd(new Date(Date.now() - Number(range) * 86400e3));
  const d = useData<Running>(env, `/admin/costs/running?from=${from}&bucket=${bucket}`, 0, paused);
  const r = d.data;
  const err = describeErr(d.error);

  const recompute = async () => {
    setBusy(true);
    try {
      const x = await post<{ refreshed: number }>(env, '/admin/costs/refresh');
      ctx.toast(`Recomputed ${x.refreshed.toLocaleString()} cost rollups`);
      d.reload();
    } catch (e) {
      ctx.toast('Recompute failed', String(e));
    } finally { setBusy(false); }
  };

  const cats = CATEGORIES.filter((c) => r?.categories.includes(c.key));
  const labels = r?.rows.map((x) => x.period) ?? [];
  const series = cats.map((c) => ({ label: c.label, bg: c.bg, vals: r?.rows.map((x) => x[c.key] ?? 0) ?? [] }));
  const keep = [{ label: 'We keep', bg: 'var(--ok)', vals: r?.rows.map((x) => Math.max(0, x.netRevenue)) ?? [] }];
  const t = r?.totals ?? {};
  const margin = t.margin ?? 0;
  const mPct = t.netRevenue ? (margin / t.netRevenue) * 100 : null;

  const rows: Row[] = (r?.rows ?? []).slice().reverse().map((x) => ({
    key: x.period,
    cells: [
      { t: x.period, mono: true },
      ...cats.map((c) => ({ t: x[c.key] ? money(x[c.key]) : '—', mono: true, fg: x[c.key] ? undefined : 'var(--ink-40)' })),
      { t: money(x.cost), mono: true, fw: 600 },
      { t: money(x.revenue), mono: true, sub: x.revenue ? `keep ${money(x.netRevenue)}` : undefined },
      { t: money(x.margin), mono: true, fw: 600, fg: x.margin < 0 ? 'var(--conf-low)' : 'var(--ok)' },
    ],
  }));
  const csv = () => {
    if (!r) return;
    const cols = ['period', ...cats.map((c) => c.key), 'enginesIdle', 'cost', 'revenue', 'netRevenue', 'margin'];
    downloadText(`running-costs-${bucket}-${from}.csv`, [cols.join(','), ...r.rows.map((x) => cols.map((k) => (k === 'period' ? x.period : (x[k] ?? 0).toFixed(6))).join(','))].join('\n') + '\n');
  };

  return (
    <>
      <Head title="Running costs" sub="What the business spends, by category, against what it earns. Fly machines and GPU pods count as they ran, idle time included; language models, voices and storage come from each transcription's cost lines. Voices are free to customers and show as their own line." />
      <Grid>
        <Cell span={12}>
          <Block title="Totals" sub={r ? `${r.from.slice(0, 10)} to ${r.to.slice(0, 10)}` : undefined} asOf={hhmmss(d.at)} loading={d.loading && !r} error={!r && err ? { ...err, retry: d.reload } : null}
            headActs={[{ label: busy ? 'Recomputing…' : 'Recompute', onClick: () => { if (!busy) void recompute(); } }]}
            extra={<><Seg<Range> value={range} onChange={setRange} options={[{ value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: 'A year' }]} /><Seg<Bucket> value={bucket} onChange={setBucket} options={[{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }]} /></>}>
            {r && <Kpis min={130} items={[
              { label: 'Revenue', v: money(t.revenue ?? 0), sub: 'before tax' },
              { label: 'We keep', v: money(t.netRevenue ?? 0), sub: `after Polar ~${Math.round(r.polarFeePct * 100)}%` },
              { label: 'Cost', v: money(t.cost ?? 0), sub: 'every category' },
              { label: 'Margin', v: money(margin), fg: margin < 0 ? 'var(--conf-low)' : 'var(--ok)', dot: margin < 0 ? 'bad' : 'ok', sub: mPct != null ? `${pct(mPct)}` : 'nothing kept' },
              { label: 'Free voices', v: money(t.voices ?? 0), sub: 'estimated' },
              { label: 'Engines idle', v: money(t.enginesIdle ?? 0), sub: 'of engines, no job' },
            ]} />}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Cost by category" sub={`Per ${bucket}, stacked; hover a bar for its numbers. In all: ${cats.filter((c) => t[c.key]).map((c) => `${c.label} ${money(t[c.key])}`).join(' · ')}.`} loading={d.loading && !r} csv={csv}>
            {r && (
              <div style={{ padding: '6px 14px 12px' }}>
                <Chart series={series} labels={labels} fmt={money} height={150} big={money(t.cost ?? 0)} bigSub="in all" />
              </div>
            )}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="What we keep" sub={`Revenue after Polar's fee, per ${bucket}. Its own scale: compare the totals, not the bar heights.`} loading={d.loading && !r}>
            {r && <div style={{ padding: '6px 14px 12px' }}><Chart series={keep} labels={labels} fmt={money} height={90} big={money(t.netRevenue ?? 0)} bigSub={`of ${money(t.revenue ?? 0)} charged`} /></div>}
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title={`Per ${bucket}`} count={String(rows.length)} csv={csv} loading={d.loading && !r} empty={r && !rows.length ? 'Nothing in this range.' : undefined}>
            <Table mobile={mobile} minWidth={1320} rows={rows} cols={[
              { label: bucket.toUpperCase(), width: '104px' },
              ...cats.map((c) => ({ label: c.short, width: 'minmax(84px,1fr)', align: 'r' as const })),
              { label: 'COST', width: 'minmax(92px,1fr)', align: 'r' }, { label: 'REVENUE', width: 'minmax(100px,1fr)', align: 'r' }, { label: 'MARGIN', width: 'minmax(92px,1fr)', align: 'r' },
            ]} />
          </Block>
        </Cell>
        {r && (
          <Cell span={12}>
            <Block title="Where each number comes from" sub="Fixed services come from the cost_model runtime setting (Settings), $ a month each; an unpriced one counts $0 until its invoice figure is entered.">
              <Table mobile={mobile} minWidth={640} rows={[
                ...cats.map((c) => ({ key: c.key, cells: [{ t: c.label, fw: 500 }, { t: r.sources[c.key] ?? '—' }] })),
                ...r.fixed.map((f) => ({ key: `fixed:${f.name}`, cells: [{ t: f.name, sub: 'fixed service' }, f.priced ? { t: `${money(f.perMonth)} a month`, mono: true } : { t: 'not priced: enter it in the cost_model setting', fg: 'var(--conf-mid)' }] })),
              ]} cols={[{ label: 'LINE', width: 'minmax(180px,1fr)' }, { label: 'BUILT FROM', width: 'minmax(320px,3fr)' }]} />
            </Block>
          </Cell>
        )}
      </Grid>
    </>
  );
}
